import React from 'react';

/**
 * Tier-agnostic SVG primitives shared by v5 long-tail schematics.
 *
 * Coordinate convention (same as v2 ApplianceFrame):
 *   parent <svg> viewBox="0 0 widthMeters heightMeters",
 *   preserveAspectRatio="xMidYMid meet",
 *   stroke widths in pixel space via vectorEffect="non-scaling-stroke".
 *
 * v5 has more "single rectangle + glyph" schematics than v2/v3 did, so
 * the SchematicShell wrapper is broken out as a thin reusable helper.
 */

export const INCH = 0.0254;

export function SchematicShell({
  width,
  height,
  strokeColor,
  strokeWidth,
  drawFrame = true,
  children,
}) {
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      {drawFrame && (
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      )}
      {children}
    </svg>
  );
}

export function Rect({
  x,
  y,
  width,
  height,
  strokeColor,
  strokeWidth,
  fill = 'none',
  fillOpacity,
}) {
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      fillOpacity={fillOpacity}
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
    />
  );
}

export function Line({ x1, y1, x2, y2, strokeColor, strokeWidth }) {
  return (
    <line
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      vectorEffect="non-scaling-stroke"
    />
  );
}

export function Circle({ cx, cy, r, strokeColor, strokeWidth, fill = 'none' }) {
  return (
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill={fill}
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      vectorEffect="non-scaling-stroke"
    />
  );
}

/**
 * Toe-kick recess line matching the v2 ApplianceFrame primitive but
 * accepting an explicit `inset` so cabinet-height objects with non-
 * standard kick heights (corner-fill, fridge surround) can override.
 */
const DEFAULT_TOE_KICK_M = 4 * INCH;
export function ToeKickLine({
  width,
  height,
  strokeColor,
  strokeWidth,
  inset = DEFAULT_TOE_KICK_M,
}) {
  if (height <= inset) return null;
  const y = height - inset;
  return (
    <Line
      x1={0}
      y1={y}
      x2={width}
      y2={y}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}
